import { Node } from '@tiptap/pm/model';
import { Selection } from '@tiptap/pm/state';
import { Editor, useEditorState } from '@tiptap/react';
import { FunctionComponent, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { IconLabel } from '../../IconLabel';

import classes from './BlockMover.module.scss';

interface BlockMoverProps {
  editor: Editor;
}

const getTopLevelRange = ({ $from, $to }: Selection) =>
  $from.blockRange($to, ({ type }) => type.name === 'doc');

const getMovableBlockCount = ({ childCount, lastChild }: Node) => {
  if (lastChild?.type.name === 'paragraph' && !lastChild.childCount) {
    return childCount - 1;
  }

  return childCount;
};

export const BlockMover: FunctionComponent<BlockMoverProps> = ({ editor }) => {
  const { t: ctxT } = useTranslation('', { keyPrefix: 'richTextEditor' });

  const { isVisible, canMoveUp, canMoveDown, from } = useEditorState({
    editor,
    selector: ({ editor: { state, isFocused } }) => {
      const range = getTopLevelRange(state.selection);

      return {
        isVisible: isFocused && range !== null,
        canMoveUp: range !== null && range.startIndex > 0,
        canMoveDown: range !== null && range.endIndex < getMovableBlockCount(state.doc),
        from: state.selection.from,
      };
    },
  });

  const [portalElement] = useState(() => document.createElement('div'));
  const [lineMiddle, setLineMiddle] = useState(0);

  const scrollToBlock = useCallback(
    (position: number) => {
      const blockElement = editor.view.nodeDOM(position);

      if (blockElement instanceof HTMLElement) {
        blockElement.scrollIntoView({ block: 'nearest' });
      }
    },
    [editor]
  );

  const moveUpHandler = useCallback(() => {
    const { state, view } = editor;
    const range = getTopLevelRange(state.selection);

    if (!range || range.startIndex === 0) {
      return;
    }

    const { start, end, startIndex } = range;
    const previousBlock = state.doc.child(startIndex - 1);
    const movedStart = start - previousBlock.nodeSize;

    view.dispatch(
      state.tr.delete(movedStart, start).insert(end - previousBlock.nodeSize, previousBlock)
    );

    scrollToBlock(movedStart);
  }, [editor, scrollToBlock]);

  const moveDownHandler = useCallback(() => {
    const { state, view } = editor;
    const range = getTopLevelRange(state.selection);

    if (!range || range.endIndex >= getMovableBlockCount(state.doc)) {
      return;
    }

    const { start, end, endIndex } = range;
    const nextBlock = state.doc.child(endIndex);

    view.dispatch(state.tr.delete(end, end + nextBlock.nodeSize).insert(start, nextBlock));

    scrollToBlock(start + nextBlock.nodeSize);
  }, [editor, scrollToBlock]);

  useEffect(() => {
    const scrollElement = editor.view.dom.parentElement;

    if (!scrollElement) {
      return;
    }

    scrollElement.appendChild(portalElement);

    return () => portalElement.remove();
  }, [editor, portalElement]);

  useLayoutEffect(() => {
    const { view } = editor;
    const scrollElement = view.dom.parentElement;

    if (!scrollElement) {
      return;
    }

    const measureHandler = () => {
      const { top, bottom } = view.coordsAtPos(from);

      setLineMiddle(
        (top + bottom) / 2 - scrollElement.getBoundingClientRect().top + scrollElement.scrollTop
      );
    };

    measureHandler();

    const resizeObserver = new ResizeObserver(measureHandler);

    resizeObserver.observe(view.dom);

    return () => resizeObserver.disconnect();
  }, [editor, from]);

  if (!isVisible) {
    return null;
  }

  return createPortal(
    <div className={classes['block-mover']} style={{ top: lineMiddle }}>
      <IconLabel
        iconId={'up-arrow'}
        ariaLabel={ctxT('block-tunes-move-up')}
        className={classes['block-mover__button']}
        onMouseDown={(event) => event.preventDefault()}
        onClick={moveUpHandler}
        disabled={!canMoveUp}
      />
      <IconLabel
        iconId={'down-arrow'}
        ariaLabel={ctxT('block-tunes-move-down')}
        className={classes['block-mover__button']}
        onMouseDown={(event) => event.preventDefault()}
        onClick={moveDownHandler}
        disabled={!canMoveDown}
      />
    </div>,
    portalElement
  );
};
